/** Sticker label geometry and text lines (pure, so it can be tested) */

export interface StickerPreset {
  id: string;
  label: string;
  width: number;
  height: number;
}

/** Common thermal label sizes (mm). The roll isn't bought yet: all of these are configurable. */
export const STICKER_PRESETS: StickerPreset[] = [
  { id: '62x40', label: '62 × 40 mm', width: 62, height: 40 },
  { id: '50x30', label: '50 × 30 mm', width: 50, height: 30 },
  { id: '62x29', label: '62 × 29 mm', width: 62, height: 29 },
  { id: '100x50', label: '100 × 50 mm', width: 100, height: 50 },
];

/** A QR smaller than this gets hard to read on thermal paper with phone cameras */
export const MIN_QR_MM = 18;

export type StickerKind = 'MINISTER' | 'DELEGATE' | 'GUEST';

export interface StickerPerson {
  token: string;
  full_name: string;
  dni?: string | null;
  adb_code?: string | null;
  district?: string | null;
  region?: string | null;
  kind?: StickerKind;
  /** PIN to vote from the phone (only when the event has an election) */
  voting_pin?: string | null;
  /** Free text under the data (kept for callers that only have a district) */
  subtitle?: string | null;
}

export const KIND_TAG: Record<StickerKind, string | null> = {
  MINISTER: null, // the common case needs no tag
  DELEGATE: 'DELEGADO',
  GUEST: 'INVITADO',
};

/** Text lines printed next to the QR so labels never get mixed up */
export function stickerLines(p: StickerPerson): { id: string; code: string; place: string; tag: string | null } {
  return {
    id: p.dni ? `CI ${p.dni}` : '',
    code: p.adb_code ?? '',
    place: [p.district, p.region].filter(Boolean).join(' · ') || p.subtitle || '',
    tag: p.kind ? KIND_TAG[p.kind] : null,
  };
}

const PADDING_MM = 2; // label padding, each side
const GAP_MM = 2; // between the text and the QR
const MAX_QR_MM = 38;
const MM_PER_PT = 0.3528;

/**
 * Layout (mm): text on the left, QR on the right using the full label height.
 * The QR is as large as the label allows (capped so very wide labels don't get a giant one).
 */
export function stickerGeometry(width: number, height: number): { qrMm: number; textWidthMm: number } {
  const qrMm = Math.min(height - 2 * PADDING_MM, width * 0.48, MAX_QR_MM);
  const textWidthMm = width - 2 * PADDING_MM - qrMm - GAP_MM;
  return { qrMm: Math.round(qrMm * 10) / 10, textWidthMm: Math.round(textWidthMm * 10) / 10 };
}

/** Number of lines `text` takes when wrapped greedily at `perLine` characters (long words are split) */
function wrappedLines(text: string, perLine: number): number {
  let lines = 1;
  let current = 0;
  for (const word of text.trim().split(/\s+/).filter(Boolean)) {
    const len = word.length;
    if (len > perLine) {
      lines += Math.ceil(len / perLine) - (current === 0 ? 1 : 0);
      current = len % perLine || perLine;
    } else if (current === 0) {
      current = len;
    } else if (current + 1 + len <= perLine) {
      current += 1 + len;
    } else {
      lines += 1;
      current = len;
    }
  }
  return lines;
}

/**
 * Largest font size (pt) at which the name fits in 3 lines (2 on short labels) of the text column,
 * so short names are big and long ones shrink instead of overflowing.
 */
export function nameFontPt(name: string, textWidthMm: number, heightMm: number): number {
  const MAX_PT = 16;
  const MIN_PT = 9;
  const maxLines = heightMm >= 35 ? 3 : 2;
  for (let pt = MAX_PT; pt >= MIN_PT; pt -= 0.5) {
    const charsPerLine = Math.floor(textWidthMm / (pt * MM_PER_PT * 0.56)); // bold: ≈ 0.56 em per character
    if (charsPerLine >= 4 && wrappedLines(name, charsPerLine) <= maxLines) return pt;
  }
  return MIN_PT;
}

export const SAMPLE_STICKER: StickerPerson = {
  token: '00000000-0000-0000-0000-000000000000',
  full_name: 'Juan Pérez Mamani',
  dni: '1234567 LP',
  adb_code: '14-2429',
  district: 'La Paz',
  region: 'Región 3',
  kind: 'MINISTER',
};
